/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   navAccessPolicy.ts
 *     Description     :   Spec 285 — THE single authoritative access policy. Maps each gated
 *                         navigation area to { required role set, optional required license,
 *                         the backend route prefixes it governs, the menu item ids it governs }.
 *                         Both the frontend menu filter and the backend requireNavAccess guard
 *                         consume `canAccess` here, so visibility == reachability (FR-001/002).
 *                         The decision: role ∈ set  AND  (no license required OR tenant holds it),
 *                         with SuperAdmin short-circuiting BOTH (full access + license-exempt),
 *                         and fail-closed on an unknown role or missing license (FR-012/021/022).
 *                         Spec 286: the decision carries the RESOLVED licence first —
 *                         decideAccess returns allow | LICENSE_UNVERIFIABLE | LICENSE_INACTIVE |
 *                         ACCESS_DENIED in the order of contracts/portal-gating.md; a pulled plug
 *                         refuses every area, Governance included (FR-007, FR-011).
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import { type Role, toRole, isSuperAdmin } from './roles';
import type { License, LicenseState, ResolvedLicense, TenantLicenses } from './licenses';

/** Stable id for a gated navigation area (menu item/group + its routes + its endpoints). */
export type NavAreaId = string;

export interface NavAccessRule {
  /** Stable area id. */
  area: NavAreaId;
  /** Explicit allowed role set. SuperAdmin is implicitly always allowed (see canAccess). */
  roles: Role[];
  /** Optional required license; when present it is required (SuperAdmin exempt). */
  license?: License;
  /** Backend route prefixes this area governs (for requireNavAccess + the parity oracle). */
  routes: string[];
  /** MenuConfig item id(s) this area governs (for menu==route parity). */
  menuItemIds: string[];
}

export type NavAccessPolicy = NavAccessRule[];

/**
 * The principal an access decision is made for. `role` may be a raw string (it is parsed).
 * `licenses` is either the resolved licence (Spec 286 — carries `state`) or the add-on flags only
 * (Spec 285 shape, no `state`): flags without a state are decided as an active licence.
 */
export interface AccessPrincipal {
  role: Role | string | null | undefined;
  licenses: TenantLicenses | ResolvedLicense;
}

/** Spec 286 — the outcome of an access decision; each refusal has its own response. */
export type AccessDecision = 'allow' | 'LICENSE_UNVERIFIABLE' | 'LICENSE_INACTIVE' | 'ACCESS_DENIED';

function licenceState(licenses: AccessPrincipal['licenses'] | null | undefined): LicenseState | undefined {
  return licenses != null && 'state' in licenses ? licenses.state : undefined;
}

/**
 * Steps 1–3 of the decision (contracts/portal-gating.md): SuperAdmin → allow (licence-exempt);
 * a licence that could not be read → LICENSE_UNVERIFIABLE; a pulled plug → LICENSE_INACTIVE, on every
 * area, Governance included (FR-007/FR-011). Otherwise 'allow' here — role and add-on come after.
 */
export function decideLicence(principal: AccessPrincipal): AccessDecision {
  const role = toRole(principal.role as string | null | undefined);
  if (role && isSuperAdmin(role)) return 'allow';
  const state = licenceState(principal.licenses);
  if (state === 'unverifiable') return 'LICENSE_UNVERIFIABLE';
  if (state === 'plugged') return 'LICENSE_INACTIVE';
  return 'allow';
}

/**
 * The one decision function, used identically by the menu filter and the route guard.
 * 1. SuperAdmin → allow (role + licence exempt), FR-004/FR-022.
 * 2–3. the licence state (decideLicence).
 * 4. role must be in the set — an unknown role is denied (fail-closed, FR-021).
 * 5. a required add-on must be in force — a missing licence object is denied.
 * 6. allow.
 */
export function decideAccess(rule: NavAccessRule, principal: AccessPrincipal): AccessDecision {
  const licence = decideLicence(principal);
  if (licence !== 'allow') return licence;
  const role = toRole(principal.role as string | null | undefined);
  if (role && isSuperAdmin(role)) return 'allow';
  if (!role || !rule.roles.includes(role)) return 'ACCESS_DENIED';
  if (!rule.license) return 'allow';
  return principal.licenses != null && principal.licenses[rule.license] === true ? 'allow' : 'ACCESS_DENIED';
}

/** The decision as a yes/no (Spec 285 callers: the menu filter, the shared-payload withholding). */
export function canAccess(rule: NavAccessRule, principal: AccessPrincipal): boolean {
  return decideAccess(rule, principal) === 'allow';
}

/**
 * The enumerated policy. Only the EXPLICITLY-GATED areas are listed; an area with no entry is
 * ungated by this feature (still behind authentication).
 *
 * `routes` are the backend paths each area's guard is registered on — the parity oracle's
 * denominator. 🛑 They were CORRECTED against a measured page→endpoint consumer map
 * (2026-10-01): the first draft named paths no page calls (`/api/platform-health`, which is
 * not mounted; `/api/sla` for SLA Blast Radius, which calls `/api/operations/sla`), and
 * missed the one the whole Backup section uses (`/api/v1/backup`). Shared prefixes are never
 * gated wholesale — `/api/v1`, `/api/operations`, `/api/users` (Workspaces' Owner dropdown),
 * `/api/clients/summary` (Manager dashboard) and the public `/api/health` stay open; only the
 * sub-paths that belong to a gated area are listed. Paths with `:param` are guarded inside
 * their router; `except`/method qualifiers are applied with the navAccess scopers.
 */
export const navAccessPolicy: NavAccessPolicy = [
  // DASHBOARDS — backup tiles only (license-gated). /api/metrics: ONLY the /backup sub-path.
  // /api/compute-footprint feeds the "Data Read by Backups" tile (Spec 213/215): backup read
  // volume — no collection writer feeds it today — so it is a backup tile (owner: gate ALL backup).
  { area: 'dashboard.backup-tiles', roles: ['Admin', 'Operator', 'Manager', 'Viewer'], license: 'backup', routes: ['/api/metrics/backup', '/api/compute-footprint'], menuItemIds: [] },

  // OPERATIONS — license-gated. Owner (2026-10-01): gate ALL backup functionality, including
  // the backup controls embedded in Resources (Protection tabs/panels, Backup History).
  {
    area: 'backup', roles: ['Admin', 'Operator', 'Manager', 'Viewer'], license: 'backup',
    routes: [
      '/api/v1/backup', '/api/v1/delta-compare', '/api/v1/reports',
      '/api/backups', '/api/warehouse-backups',
      // legacy backup-module endpoints with no live UI caller that still serve backup data
      '/api/v1/dashboard', '/api/v1/policies', '/api/v1/schedules', '/api/v1/retention-policies',
      '/api/v1/backup-runs', '/api/v1/restore-runs', '/api/v1/recovery-tests', '/api/v1/storage-metrics',
      '/api/v1/cleanup-runs', '/api/v1/remote', '/api/v1/discovery', '/api/v1/settings',
      // backup controls on the shared lakehouses router
      '/api/v1/lakehouses/:id/enable-backup', '/api/v1/lakehouses/:id/disable-backup',
      '/api/v1/lakehouses/:id/protection-tier', '/api/v1/lakehouses/:id/policy',
    ],
    menuItemIds: ['backup'],
  },
  { area: 'pipeline-monitoring', roles: ['Admin', 'Operator', 'Manager', 'Viewer'], license: 'ops', routes: ['/api/pipeline-monitoring'], menuItemIds: ['pipeline-monitoring'] },
  // Owner, 2026-10-01: "Workflows fall under the backup license rule. You only need this when you
  // have backups." Workflows track backup onboarding; the whole /api/workflows prefix is theirs
  // (the only outside callers, the lakehouse/warehouse detail banners, are already backup-gated).
  { area: 'operations.workflows', roles: ['Admin', 'Operator', 'Manager', 'Viewer'], license: 'backup', routes: ['/api/workflows'], menuItemIds: ['workflows'] },
  { area: 'data-pipeline-monitoring', roles: ['SuperAdmin'], routes: ['/api/monitored-pipelines'], menuItemIds: ['ods-monitoring'] },
  { area: 'platform-health', roles: ['SuperAdmin'], routes: ['/api/operations/platform'], menuItemIds: ['platform-health'] },
  // Same SuperAdmin-only rule as internal.configuration, which guards the whole /api/infrastructure mount.
  { area: 'infrastructure-health', roles: ['SuperAdmin'], routes: ['/api/infrastructure/health'], menuItemIds: ['infrastructure-health'] },

  // CLIENTS — SuperAdmin only. /api/clients EXCEPT /summary. Owner (2026-10-01): client
  // assignment from Workspace Insights is SuperAdmin-only too.
  {
    area: 'clients.client-list', roles: ['SuperAdmin'],
    routes: [
      '/api/clients', '/api/v1/:clientId/capacities', '/api/v1/:clientId/workspaces',
      '/api/workspace-insights/clients', '/api/workspace-insights/:id/client',
    ],
    menuItemIds: ['clients'],
  },
  { area: 'clients.sla-blast-radius', roles: ['SuperAdmin'], routes: ['/api/operations/sla'], menuItemIds: ['sla-blast-radius'] },
  { area: 'clients.sla-configuration', roles: ['SuperAdmin'], routes: ['/api/sla'], menuItemIds: ['settings-sla'] },

  // ANALYTICS — SuperAdmin only
  { area: 'analytics.metadata', roles: ['SuperAdmin'], routes: ['/api/metadata', '/api/metadata-insights'], menuItemIds: ['metadata'] },

  // SYSTEM — SuperAdmin only, except Users. NOT /api/users (it feeds the Workspaces Owner
  // dropdown; owner kept it open to Admin, DD-890 2026-10-02).
  // Users → Admin (owner, DD-890 2026-10-02): "Clients manage their users in FCM and on FCP the
  // admin can overwrite the email preferences. But not the kill switch for the emails, that one is
  // superadmin." Every route here is a read except PUT notification-preferences/users/:id, which
  // refuses a user outside the caller's tenant. The kill switch lives in internal.configuration.
  {
    area: 'system.users', roles: ['Admin'],
    routes: [
      '/api/v1/users', '/api/auth/users',
      '/api/notification-stats', '/api/notification-preferences/summary', '/api/notification-preferences/users',
    ],
    menuItemIds: ['users'],
  },
  { area: 'system.system-logs', roles: ['SuperAdmin'], routes: ['/api/audit', '/api/audit-logs'], menuItemIds: ['system-logs'] },
  { area: 'system.alert-rules', roles: ['SuperAdmin'], routes: ['/api/alert-rules'], menuItemIds: ['settings-alert-rules'] },
  { area: 'system.tenant-licenses', roles: ['SuperAdmin'], routes: ['/api/admin/tenant-licenses'], menuItemIds: ['tenant-licenses'] },

  // INTERNAL — Collection Jobs + Infra Sync for Admin; the rest SuperAdmin only.
  // /api/jobs EXCEPT /callback (container callbacks). /api/deployment-history GET only (CI POSTs).
  {
    area: 'internal.collection-jobs', roles: ['SuperAdmin', 'Admin'],
    routes: ['/api/jobs', '/api/batch-jobs', '/api/operations/collection-jobs', '/api/operations/fcjlogs', '/api/collection-integrity'],
    menuItemIds: ['jobs'],
  },
  { area: 'internal.infrastructure-sync', roles: ['SuperAdmin', 'Admin'], routes: ['/api/infrastructure-sync', '/api/discovery'], menuItemIds: ['discovery'] },
  { area: 'internal.deployment-history', roles: ['SuperAdmin'], routes: ['/api/deployment-history'], menuItemIds: ['deployment-history'] },
  { area: 'internal.maintenance', roles: ['SuperAdmin'], routes: ['/api/v1/maintenance'], menuItemIds: ['maintenance'] },
  { area: 'internal.reconciliation', roles: ['SuperAdmin'], routes: ['/api/reconciliation'], menuItemIds: ['reconciliation'] },
  { area: 'internal.configuration', roles: ['SuperAdmin'], routes: ['/api/infrastructure', '/api/settings'], menuItemIds: ['infrastructure-config'] },
  { area: 'internal.service-principals', roles: ['SuperAdmin'], routes: ['/api/v1/service-principals'], menuItemIds: ['service-principals'] },
  // Same SuperAdmin-only rule, served under the same prefix (guarded once, as service-principals).
  { area: 'internal.sp-reconciliation', roles: ['SuperAdmin'], routes: ['/api/v1/service-principals'], menuItemIds: ['service-principals-reconciliation'] },
];

const byArea: Map<NavAreaId, NavAccessRule> = new Map(navAccessPolicy.map((r) => [r.area, r]));

/** Look up a rule by area id. */
export function policyFor(area: NavAreaId): NavAccessRule | undefined {
  return byArea.get(area);
}

/**
 * Decide access for an area id. An UNKNOWN area id (no policy entry) is ungated by role and add-on
 * (the gated areas are exactly those enumerated above) but still obeys the licence state (Spec 286).
 */
export function decideAccessForArea(area: NavAreaId, principal: AccessPrincipal): AccessDecision {
  const rule = byArea.get(area);
  return rule ? decideAccess(rule, principal) : decideLicence(principal);
}

export function canAccessArea(area: NavAreaId, principal: AccessPrincipal): boolean {
  return decideAccessForArea(area, principal) === 'allow';
}
